/**
 * Register Page
 * Handles both self-registration and invitation acceptance
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { apiClient } from '../lib/api-client';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, CheckCircle, Info } from 'lucide-react';
import type { User } from '../types/api';

interface ValidateTokenResponse {
  valid: boolean;
  user?: Omit<User, 'password_hash'>;
}

export function RegisterPage() {
  const { token } = useParams<{ token?: string }>();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [invitedUser, setInvitedUser] = useState<Omit<User, 'password_hash'> | null>(null);
  const navigate = useNavigate();
  const register = useAuthStore(s => s.register);

  // Validate invitation token if present
  useEffect(() => {
    if (token) {
      validateInvitationToken();
    }
  }, [token]);

  async function validateInvitationToken() {
    setIsValidatingToken(true);
    try {
      const response = await apiClient.get<ValidateTokenResponse>(
        `/auth/validate-token/${token}`
      );

      if (response.valid && response.user) {
        setInvitedUser(response.user);
        setEmail(response.user.email);
        setName(response.user.name);
      } else {
        setError('Invalid or expired invitation link');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate invitation');
    } finally {
      setIsValidatingToken(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      if (token) {
        // Invitation acceptance flow - auto-login
        await register({ email, name, password }, token);

        setSuccess('Account activated successfully! Redirecting...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        // Self-registration flow
        await apiClient.post('/auth/register', { email, name, password });
        setSuccess('Registration submitted! Your account is awaiting administrator approval.');

        // Redirect to login after delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  }

  if (isValidatingToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-sm/5 text-muted-foreground">
              Validating invitation...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col gap-2">
          <CardTitle className="text-2xl/tight font-bold">
            {token ? 'Complete Registration' : 'Register'}
          </CardTitle>
          <CardDescription className="text-sm/5">
            {token
              ? 'Complete your account setup'
              : 'Create a new account (subject to approval)'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-4 border-success-500/20 bg-success-100 text-success-700">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {invitedUser && (
                <Alert className="border-info-500/20 bg-info-100 text-info-700">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="font-medium">
                    You've been invited as: {invitedUser.role}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading || !!token}
                  readOnly={!!token}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Registering...' : 'Register'}
              </Button>

              {!token && (
                <p className="text-center text-sm/5 text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary hover:underline">
                    Login
                  </Link>
                </p>
              )}
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
